import { Fragment, useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { GrundartikelLink } from "@/components/catalog/GrundartikelLink";
import { Search, X, ChevronLeft, PackageSearch } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Panel } from "@/components/Panel";
import { RangeFilter } from "@/components/RangeFilter";
import { ExportCsvButton } from "@/components/ExportCsvButton";
import { buildTopCsv, buildArticleSeriesCsv } from "@/lib/analytics-csv";
import { StackedBarChart, CHANNEL_COLORS } from "@/components/charts/StackedBarChart";
import { ChannelLineChart } from "@/components/charts/ChannelLineChart";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  useGaSearch,
  useArticleSeries,
  useTop,
  DASH_CHANNELS,
  RETURNS_RATE_MIN_SOLD,
  type DashRange,
  type DashChannel,
  type TopMetric,
  type TopRow,
  type GaSearchItem,
} from "@/api/dashboard";
import { DASH_RANGE_LABELS, formatEuro, sumDashBuckets, needsHistoryHint, HISTORY_HINT_TEXT } from "@/lib/dash-format";
import { UNIT_LABELS, formatBucketLabel } from "@/lib/timeseries-format";
import {
  isSearchable,
  toArticleStackedBuckets,
  pushCategory,
  popCategoryTo,
  currentCategoryParent,
} from "@/lib/article-analysis";

const METRIC_LABELS: Record<TopMetric, string> = { qty: "Menge", revenue: "Umsatz" };

function formatMetricValue(metric: TopMetric, n: number): string {
  return metric === "revenue" ? formatEuro(n) : n.toLocaleString("de-DE");
}

// ============================================================
//  Metrik-Umschalter (Menge/Umsatz) — kleine, wiederverwendete Segmented-Control, exakt
//  im Look des metric-Umschalters aus pages/overview/Overview.tsx (Task 15), nur ohne die
//  dort vorhandenen 4 Werte (hier immer genau 2: Menge/Umsatz).
// ============================================================
function MetricToggle({ value, onChange }: { value: TopMetric; onChange: (m: TopMetric) => void }) {
  return (
    <div className="flex p-0.5 rounded-md bg-panel-2 border border-line">
      {(["qty", "revenue"] as const).map((m) => (
        <button
          key={m}
          type="button"
          onClick={() => onChange(m)}
          aria-pressed={value === m}
          className={`px-2.5 py-1 text-xs rounded transition-colors ${
            value === m ? "bg-background text-ink shadow-sm" : "text-ink-2 hover:text-ink"
          }`}
        >
          {METRIC_LABELS[m]}
        </button>
      ))}
    </div>
  );
}

// Rang-Zeile fuer die drei Ranglisten-Panels — gemeinsames Layout (Rang, Label, Sublabel,
// Wert, Prozent-Balken), Overview-Top-Produkte-Muster (Task 15) auf drei Dimensionen
// verallgemeinert. `onClick` macht die Zeile nur fuer den Kategorien-Drilldown interaktiv.
function TopRowItem({
  rank,
  row,
  metric,
  onClick,
}: {
  rank: number;
  row: TopRow;
  metric: TopMetric;
  onClick?: () => void;
}) {
  const value = metric === "revenue" ? row.value : row.qty;
  const content = (
    <>
      <span className="font-mono text-xs text-muted w-6 shrink-0">{String(rank).padStart(2, "0")}</span>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-ink truncate" title={row.label || "Ohne Bezeichnung"}>
          {row.label || "Ohne Bezeichnung"}
        </div>
        {row.sublabel && <div className="text-[11px] text-ink-2 font-mono truncate">{row.sublabel}</div>}
        {/* W3 (#9): nur die Retouren-Liste liefert soldQty — Quote und Bezugsmenge stehen
            direkt an der Zeile, damit "12 Retouren" nicht ohne Bezugsgroesse dasteht. */}
        {row.soldQty !== null && (
          <div className="text-[11px] text-ink-2 font-mono truncate">
            {row.returnRate !== null ? (
              <span className="text-ink">Quote {row.returnRate.toFixed(1).replace(".", ",")} %</span>
            ) : (
              <span title={`Die Quote wird erst ab ${RETURNS_RATE_MIN_SOLD} verkauften Stück berechnet.`}>
                Quote —
              </span>
            )}
            {" · "}
            {row.soldQty} verkauft
          </div>
        )}
        <div className="mt-1 h-1 bg-panel-2 rounded">
          <div className="h-full bg-primary rounded" style={{ width: `${Math.min(100, Math.max(0, row.pct))}%` }} />
        </div>
      </div>
      <div className="text-right shrink-0">
        <div className="font-mono text-sm text-ink font-semibold">{formatMetricValue(metric, value)}</div>
        <div className="text-[10px] text-muted font-mono">{row.pct.toFixed(0)}%</div>
      </div>
    </>
  );
  if (onClick) {
    return (
      <li>
        <button
          type="button"
          onClick={onClick}
          className="w-full flex items-center gap-3 py-2.5 text-left hover:bg-panel-2 rounded-md px-1.5 -mx-1.5 transition-colors"
        >
          {content}
        </button>
      </li>
    );
  }
  return <li className="flex items-center gap-3 py-2.5">{content}</li>;
}

// ============================================================
//  GA-Suche mit Vorschlagsliste. Feuert erst ab MIN_SEARCH_LENGTH Zeichen (lib/article-
//  analysis.ts) und erst 300ms nach dem letzten Tastendruck (Server-Suche-Debounce-
//  Konvention aus pages/stock/StockList.tsx). Auswahl setzt gaId + zeigt den Artikel-Kopf.
// ============================================================
function ArticleSearch({ onSelect }: { onSelect: (item: GaSearchItem) => void }) {
  const [input, setInput] = useState("");
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setQuery(isSearchable(input) ? input.trim() : ""), 300);
    return () => clearTimeout(t);
  }, [input]);

  const { data, isLoading, isError } = useGaSearch(query);
  const suggestions = data?.items ?? [];

  useEffect(() => setActiveIndex(-1), [query, suggestions.length]);

  useEffect(() => {
    function onOutside(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, []);

  function pick(item: GaSearchItem) {
    onSelect(item);
    setInput("");
    setQuery("");
    setOpen(false);
    setActiveIndex(-1);
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === "Enter") {
      if (activeIndex >= 0 && suggestions[activeIndex]) {
        e.preventDefault();
        pick(suggestions[activeIndex]);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  }

  const showDropdown = open && isSearchable(input);

  return (
    <div ref={rootRef} className="relative w-full max-w-md">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted pointer-events-none" />
        <Input
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder="Sternnummer, SKU oder Name suchen…"
          aria-label="Grundartikel suchen"
          aria-autocomplete="list"
          aria-expanded={showDropdown}
          aria-controls="ga-search-listbox"
          className="pl-9"
        />
      </div>

      {showDropdown && (
        <div
          id="ga-search-listbox"
          role="listbox"
          aria-label="Suchergebnisse"
          className="absolute z-20 mt-1 w-full rounded-md border border-line bg-panel shadow-lg max-h-80 overflow-y-auto"
        >
          {isError ? (
            <div className="px-3 py-3 text-sm text-coral">Suche nicht erreichbar.</div>
          ) : isLoading ? (
            <div className="px-3 py-3 text-sm text-muted">Suche…</div>
          ) : suggestions.length === 0 ? (
            <div className="px-3 py-3 text-sm text-muted">Keine Treffer für „{input.trim()}".</div>
          ) : (
            <ul>
              {suggestions.map((item, i) => (
                // role="option" sitzt direkt auf dem klickbaren Element (nicht auf einem
                // Wrapper mit verschachteltem <button> darin) — ARIA-Listbox-Options
                // duerfen kein weiteres interaktives Element enthalten, und ein Klick auf
                // den Wrapper wuerde den Handler eines Kind-<button> ohnehin nie ausloesen
                // (Click bubbelt nach oben, nicht nach unten).
                <li
                  key={item.id}
                  role="option"
                  aria-selected={i === activeIndex}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pick(item)}
                  className={`flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors ${
                    i === activeIndex ? "bg-panel-2" : "hover:bg-panel-2"
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-ink truncate">{item.name}</div>
                    <div className="text-[11px] text-ink-2 font-mono">
                      {item.merchant_sku ? `${item.merchant_sku} · ${item.sku}` : item.sku}
                    </div>
                  </div>
                  <div className="font-mono text-xs text-muted shrink-0">
                    {item.quantity.toLocaleString("de-DE")} Stk.
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export default function ArticleAnalysis() {
  // ---- gewählter Artikel ----
  const [gaId, setGaId] = useState<number | null>(null);
  const [selectedArticle, setSelectedArticle] = useState<GaSearchItem | null>(null);

  function selectArticle(item: GaSearchItem) {
    setGaId(item.id);
    setSelectedArticle(item);
  }
  function clearArticle() {
    setGaId(null);
    setSelectedArticle(null);
  }

  // ---- Kanal-Verlauf des gewählten Artikels ----
  const [articleRange, setArticleRange] = useState<DashRange>("30T");
  const [articleMetric, setArticleMetric] = useState<TopMetric>("qty");
  const [chartMode, setChartMode] = useState<"bars" | "lines">("bars");
  const { data: series, isLoading: seriesLoading, isError: seriesErrored } = useArticleSeries(gaId, articleRange);

  const seriesChannels = series?.channels ?? [];
  const seriesUnit = series?.unit ?? "day";
  const transformedBuckets = useMemo(
    () => toArticleStackedBuckets(series?.buckets ?? [], articleMetric),
    [series?.buckets, articleMetric],
  );
  const displayBuckets = useMemo(
    () => transformedBuckets.map((b) => ({ ...b, label: formatBucketLabel(b.label, seriesUnit) })),
    [transformedBuckets, seriesUnit],
  );
  const seriesSum = useMemo(
    () => sumDashBuckets(transformedBuckets, seriesChannels),
    [transformedBuckets, seriesChannels],
  );
  const seriesAvg = transformedBuckets.length > 0 ? seriesSum / transformedBuckets.length : 0;

  // ---- Ranglisten (unabhängig vom gewählten Artikel) ----
  const [topRange, setTopRange] = useState<DashRange>("30T");

  const [gaMetric, setGaMetric] = useState<TopMetric>("qty");
  const { data: topGaData, isLoading: gaLoading, isError: gaErrored } = useTop("ga", topRange, gaMetric);
  const topGaRows = topGaData?.rows ?? [];

  const [categoryMetric, setCategoryMetric] = useState<TopMetric>("qty");
  const [categoryPath, setCategoryPath] = useState<TopRow[]>([]);
  const categoryParent = currentCategoryParent(categoryPath);
  const {
    data: topCategoryData,
    isLoading: categoryLoading,
    isError: categoryErrored,
  } = useTop("category", topRange, categoryMetric, categoryParent);
  const topCategoryRows = topCategoryData?.rows ?? [];

  const [returnsChannel, setReturnsChannel] = useState<DashChannel | null>(null);
  const {
    data: topReturnsData,
    isLoading: returnsLoading,
    isError: returnsErrored,
  } = useTop("returns", topRange, "qty", null, returnsChannel);
  const topReturnsRows = topReturnsData?.rows ?? [];

  return (
    <div className="p-8 space-y-5 max-w-[1400px]">
      <header>
        <h1 className="font-display font-bold text-2xl text-ink">Artikel-Analyse</h1>
        <p className="text-sm text-ink-2 mt-1">
          Verkaufsentwicklung je Kanal für einen einzelnen Grundartikel, plus Ranglisten für Sortiment und
          Retouren
        </p>
      </header>

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="space-y-1.5">
          <label className="text-xs text-ink-2 uppercase tracking-wide">Grundartikel wählen</label>
          <ArticleSearch onSelect={selectArticle} />
        </div>
      </div>

      {!selectedArticle ? (
        <Panel title="Kein Artikel gewählt" subtitle="Nutze die Suche oben, um einen Grundartikel zu analysieren">
          <div className="flex flex-col items-center justify-center gap-3 py-10 text-center">
            <PackageSearch className="size-8 text-muted" />
            <p className="text-sm text-ink-2 max-w-sm">
              Suche nach Sternnummer (z. B. „*1536"), SKU oder Artikelname, um den Kanal-Verlauf und die
              Stammdaten eines Grundartikels zu sehen. Die Ranglisten unten funktionieren auch ohne
              Auswahl.
            </p>
          </div>
        </Panel>
      ) : (
        <>
          <Panel
            title={selectedArticle.name}
            subtitle="Grundartikel-Stammdaten"
            actions={
              <Button variant="ghost" size="sm" onClick={clearArticle} className="gap-1.5">
                <X className="size-3.5" />
                Auswahl aufheben
              </Button>
            }
          >
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div>
                <div className="text-xs text-ink-2 uppercase tracking-wide">SKU</div>
                <div className="font-mono text-sm text-ink mt-1">{selectedArticle.sku}</div>
              </div>
              <div>
                <div className="text-xs text-ink-2 uppercase tracking-wide">Sternnummer</div>
                <div className="font-mono text-sm text-ink mt-1"><GrundartikelLink code={selectedArticle.merchant_sku} /></div>
              </div>
              <div className="col-span-2 sm:col-span-2">
                <div className="text-xs text-ink-2 uppercase tracking-wide">Bestand</div>
                <div className="font-display font-bold text-xl text-ink mt-0.5">
                  {selectedArticle.quantity.toLocaleString("de-DE")}
                  <span className="text-xs font-normal text-muted ml-1">Stück</span>
                </div>
              </div>
            </div>
          </Panel>

          <Panel
            title={`Kanal-Verlauf · ${METRIC_LABELS[articleMetric]}`}
            subtitle={`${DASH_RANGE_LABELS[articleRange]} · ${selectedArticle.name}`}
            actions={
              <div className="flex flex-wrap items-center justify-end gap-2">
                <RangeFilter value={articleRange} onChange={setArticleRange} label="Zeitraum Kanal-Verlauf" />
                <MetricToggle value={articleMetric} onChange={setArticleMetric} />
                <div className="flex p-0.5 rounded-md bg-panel-2 border border-line">
                  {([["bars", "Balken"], ["lines", "Linien"]] as const).map(([m, label]) => (
                    <button key={m} type="button" onClick={() => setChartMode(m)} aria-pressed={chartMode === m}
                      className={`px-2.5 py-1 text-xs rounded transition-colors ${chartMode === m ? "bg-background text-ink shadow-sm" : "text-ink-2 hover:text-ink"}`}>
                      {label}
                    </button>
                  ))}
                </div>
                <ExportCsvButton filename="kanal-verlauf" build={() => buildArticleSeriesCsv(series?.buckets ?? [], seriesChannels)} disabled={(series?.buckets ?? []).length === 0} />
              </div>
            }
          >
            {seriesChannels.length > 0 && (
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-ink-2 mb-3">
                {seriesChannels.map((ch) => (
                  <span key={ch} className="inline-flex items-center gap-1.5">
                    <span className="size-2 rounded-sm" style={{ background: CHANNEL_COLORS[ch] ?? CHANNEL_COLORS.Sonstige }} />
                    {ch}
                  </span>
                ))}
              </div>
            )}
            {needsHistoryHint(articleRange) && (
              <div className="text-[11px] text-ink-2 italic mb-3">{HISTORY_HINT_TEXT}</div>
            )}

            {seriesErrored ? (
              <div className="text-sm text-coral py-8 text-center">Kanal-Verlauf nicht erreichbar.</div>
            ) : seriesLoading && displayBuckets.length === 0 ? (
              <div className="text-sm text-muted py-8 text-center">Lade Kanal-Verlauf…</div>
            ) : displayBuckets.length === 0 ? (
              <div className="text-sm text-muted py-8 text-center">Keine Verkäufe im gewählten Zeitraum.</div>
            ) : (
              <>
                {chartMode === "bars" ? (
                  <StackedBarChart
                    buckets={displayBuckets}
                    channels={seriesChannels}
                    formatValue={(n) => formatMetricValue(articleMetric, n)}
                  />
                ) : (
                  <ChannelLineChart
                    buckets={displayBuckets}
                    channels={seriesChannels}
                    formatValue={(n) => formatMetricValue(articleMetric, n)}
                  />
                )}
                <div className="mt-3 text-xs text-muted font-mono text-right">
                  Summe: {formatMetricValue(articleMetric, seriesSum)} · Ø{" "}
                  {formatMetricValue(articleMetric, seriesAvg)} / {UNIT_LABELS[seriesUnit]}
                </div>
              </>
            )}
          </Panel>
        </>
      )}

      <div className="flex items-end justify-between gap-3 pt-2">
        <div>
          <h2 className="font-display font-semibold text-lg text-ink">Ranglisten</h2>
          <p className="text-xs text-ink-2 mt-0.5">Unabhängig vom oben gewählten Artikel</p>
        </div>
        <RangeFilter value={topRange} onChange={setTopRange} label="Zeitraum Ranglisten" />
      </div>
      {needsHistoryHint(topRange) && (
        <div className="text-[11px] text-ink-2 italic mb-3">{HISTORY_HINT_TEXT}</div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Panel
          title="Top-Grundartikel"
          subtitle={`Nach ${METRIC_LABELS[gaMetric]} · ${DASH_RANGE_LABELS[topRange]}`}
          actions={
            <div className="flex items-center gap-2">
              <MetricToggle value={gaMetric} onChange={setGaMetric} />
              <ExportCsvButton filename="top-grundartikel" build={() => buildTopCsv(topGaRows)} disabled={topGaRows.length === 0} />
            </div>
          }
        >
          {gaErrored ? (
            <div className="text-sm text-coral py-4">Top-Grundartikel nicht erreichbar.</div>
          ) : gaLoading && topGaRows.length === 0 ? (
            <div className="text-sm text-muted py-4">Lädt…</div>
          ) : topGaRows.length === 0 ? (
            <div className="text-sm text-muted py-4">Keine Verkäufe im gewählten Zeitraum.</div>
          ) : (
            <ul className="divide-y divide-line-2">
              {topGaRows.map((row, i) => (
                <TopRowItem key={row.id ?? `${row.label}-${i}`} rank={i + 1} row={row} metric={gaMetric} />
              ))}
            </ul>
          )}
        </Panel>

        <Panel
          title="Top-Kategorien"
          subtitle={`Nach ${METRIC_LABELS[categoryMetric]} · ${DASH_RANGE_LABELS[topRange]}`}
          actions={
            <div className="flex items-center gap-2">
              <MetricToggle value={categoryMetric} onChange={setCategoryMetric} />
              <ExportCsvButton filename="top-kategorien" build={() => buildTopCsv(topCategoryRows)} disabled={topCategoryRows.length === 0} />
            </div>
          }
        >
          <div className="flex items-center gap-2 mb-3">
            {categoryPath.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="gap-1 shrink-0 -ml-1.5"
                onClick={() => setCategoryPath(popCategoryTo(categoryPath, categoryPath.length - 2))}
              >
                <ChevronLeft className="size-3.5" />
                Zurück
              </Button>
            )}
            <Breadcrumb>
              <BreadcrumbList className="text-xs flex-nowrap">
                <BreadcrumbItem>
                  {categoryPath.length === 0 ? (
                    <BreadcrumbPage className="text-ink">Alle Kategorien</BreadcrumbPage>
                  ) : (
                    <BreadcrumbLink asChild>
                      <button type="button" onClick={() => setCategoryPath(popCategoryTo(categoryPath, -1))}>
                        Alle Kategorien
                      </button>
                    </BreadcrumbLink>
                  )}
                </BreadcrumbItem>
                {categoryPath.map((row, i) => (
                  <Fragment key={row.id ?? i}>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                      {i === categoryPath.length - 1 ? (
                        <BreadcrumbPage className="text-ink truncate max-w-32">{row.label}</BreadcrumbPage>
                      ) : (
                        <BreadcrumbLink asChild>
                          <button type="button" onClick={() => setCategoryPath(popCategoryTo(categoryPath, i))}>
                            {row.label}
                          </button>
                        </BreadcrumbLink>
                      )}
                    </BreadcrumbItem>
                  </Fragment>
                ))}
              </BreadcrumbList>
            </Breadcrumb>
          </div>

          {categoryErrored ? (
            <div className="text-sm text-coral py-4">Top-Kategorien nicht erreichbar.</div>
          ) : categoryLoading && topCategoryRows.length === 0 ? (
            <div className="text-sm text-muted py-4">Lädt…</div>
          ) : topCategoryRows.length === 0 ? (
            <div className="text-sm text-muted py-4">
              Keine Unterkategorien mit Verkäufen im gewählten Zeitraum.
            </div>
          ) : (
            <ul className="divide-y divide-line-2">
              {topCategoryRows.map((row, i) => (
                <TopRowItem
                  key={row.id ?? `${row.label}-${i}`}
                  rank={i + 1}
                  row={row}
                  metric={categoryMetric}
                  onClick={() => setCategoryPath(pushCategory(categoryPath, row))}
                />
              ))}
            </ul>
          )}
        </Panel>
      </div>

      <Panel
        title="Top-Retouren"
        subtitle={`Am häufigsten zurückgeschickt · ${DASH_RANGE_LABELS[topRange]}`}
        actions={
          <div className="flex items-center gap-2">
            <select
              value={returnsChannel ?? ""}
              onChange={(e) => setReturnsChannel(e.target.value === "" ? null : (e.target.value as DashChannel))}
              className="text-xs rounded border border-line bg-background text-ink px-2 py-1"
              aria-label="Verkaufskanal filtern"
            >
              <option value="">Alle Kanäle</option>
              {DASH_CHANNELS.map((ch) => (
                <option key={ch} value={ch}>
                  {ch}
                </option>
              ))}
            </select>
            <ExportCsvButton filename="top-retouren" build={() => buildTopCsv(topReturnsRows)} disabled={topReturnsRows.length === 0} />
          </div>
        }
      >
        {returnsErrored ? (
          <div className="text-sm text-coral py-4">Top-Retouren nicht erreichbar.</div>
        ) : returnsLoading && topReturnsRows.length === 0 ? (
          <div className="text-sm text-muted py-4">Lädt…</div>
        ) : topReturnsRows.length === 0 ? (
          <div className="text-sm text-muted py-4">
            {returnsChannel
              ? `Keine Retouren über ${returnsChannel} im gewählten Zeitraum.`
              : "Keine Retouren im gewählten Zeitraum."}
          </div>
        ) : (
          <ul className="divide-y divide-line-2">
            {topReturnsRows.map((row, i) => (
              <TopRowItem key={row.id ?? `${row.label}-${i}`} rank={i + 1} row={row} metric="qty" />
            ))}
          </ul>
        )}
      </Panel>
    </div>
  );
}
